// src/admin/components/integrations/IntegrationActivityPanel.jsx
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import DateTimeDisplay from '../common/DateTimeDisplay';
import StatusPill from '../../../shared/components/pill/StatusPill';
import { fetchWithAuth } from '../../auth/fetchWithAuth';
import { API_URL } from '../../../config/env';

const STATUS_BADGES = {
  delivered: { label: 'Delivered', tone: 'success' },
  pending: { label: 'Pending', tone: 'neutral' },
  failed: { label: 'Failed', tone: 'danger' },
  skipped: { label: 'Skipped', tone: 'quiet' },
  abandoned: { label: 'Abandoned', tone: 'quiet' },
};

const EVENT_LABELS = {
  'task.assigned': 'Task assigned',
  'task.started': 'Task started',
  'task.completed': 'Task completed',
};

/**
 * Outbound delivery log for a ticketing integration.
 *
 * This is the operator's evidence that nothing has been lost. Given that customers attach SLA
 * counters to these events, "what have we not told the ticketing system?" has to be answerable
 * here rather than from application logs.
 */
export default function IntegrationActivityPanel({ integrationId }) {
  const [events, setEvents] = useState([]);
  const [outstanding, setOutstanding] = useState(0);
  const [onlyOutstanding, setOnlyOutstanding] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!integrationId) return;
    setLoading(true);
    setError('');
    try {
      const query = onlyOutstanding ? '?outstanding=true' : '';
      const res = await fetchWithAuth(
        `${API_URL}/v1/admin/integrations/${integrationId}/ticketing_activity${query}`,
        { cache: 'no-store' },
      );
      if (!res.ok) throw new Error('Could not load activity');
      const body = await res.json();
      setEvents(body.events || []);
      setOutstanding(body.outstanding_count || 0);
    } catch (err) {
      setError(err.message || 'Could not load activity');
    } finally {
      setLoading(false);
    }
  }, [integrationId, onlyOutstanding]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          {outstanding > 0 ? (
            <StatusPill tone="warning">{`${outstanding} not yet delivered`}</StatusPill>
          ) : (
            <span className="text-muted small">Everything delivered</span>
          )}
        </div>
        <div className="form-check form-switch mb-0">
          <input
            className="form-check-input"
            type="checkbox"
            id="activity-only-outstanding"
            checked={onlyOutstanding}
            onChange={(e) => setOnlyOutstanding(e.target.checked)}
          />
          <label className="form-check-label small" htmlFor="activity-only-outstanding">
            Undelivered only
          </label>
        </div>
      </div>

      {error && <div className="alert alert-danger py-2 small" role="alert">{error}</div>}
      {loading && <div className="text-muted small">Loading activity…</div>}
      {!loading && events.length === 0 && !error && (
        <p className="text-muted small mb-0">No ticket updates yet.</p>
      )}

      <ul className="list-unstyled mb-0">
        {events.map((event) => {
          const badge = STATUS_BADGES[event.status] || STATUS_BADGES.pending;
          return (
            <li key={event.id} className="border-bottom py-2">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <span className="fw-semibold">{EVENT_LABELS[event.event] || event.event}</span>
                  {event.ticket_reference && (
                    <span className="ms-2">
                      {event.ticket_url ? (
                        <a href={event.ticket_url} target="_blank" rel="noreferrer">
                          {event.ticket_reference}
                        </a>
                      ) : event.ticket_reference}
                    </span>
                  )}
                </div>
                <StatusPill tone={badge.tone}>{badge.label}</StatusPill>
              </div>
              <div className="text-muted small">
                Occurred <DateTimeDisplay isoString={event.occurred_at} />
                {event.delivered_at && (
                  <>
                    {' · delivered '}
                    <DateTimeDisplay isoString={event.delivered_at} />
                  </>
                )}
                {event.attempts > 0 && event.status !== 'delivered' && (
                  <>{` · ${event.attempts} attempt${event.attempts === 1 ? '' : 's'}`}</>
                )}
              </div>
              {event.last_error && (
                <div className="small text-danger-emphasis mt-1">{event.last_error}</div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

IntegrationActivityPanel.propTypes = {
  integrationId: PropTypes.number,
};
IntegrationActivityPanel.defaultProps = { integrationId: null };
